import { useState, useEffect } from "react";
import ReactDatePicker from "react-datepicker";
import { Button, Dropdown, DropdownItemProps, Form, FormProps, Input, Modal, Segment, Table, TextArea } from "semantic-ui-react";
import { dialogControl } from "../../controls/dialog-control";
import { useCurrentUser } from "../../hooks/auth-hook";
import { ModalControlData, useModalControl } from "../../hooks/commonHooks";
import { useFormConnect, useFormData } from "../../hooks/formHooks";
import { hostUrl, restRequest } from "../../http";
import { PageData } from "../../models/common_model";
import { DropdownOption } from "../../models/testing_model";
import { AnalyzedUnit, AnalyzedUnitConfig } from "../../models/analyzed_unit_model";
import { fieldError, formatDateTime } from "../../utils";
import { JournalizationComponent } from "../commons/JournalizationComponent";
import { Page } from "../commons/Page";
import { AnalyzedUnitEditor } from "../AnalyzedUnitEditor";

export function PhoneWaiveUnitComponent(props: {}) {
    const currentUser = useCurrentUser();

    const [searchData, setSearchField] = useFormData<any>({ product: '', station: '', order: '', measurement_status: [1, 2, 3], updated_status: [0, 1, 2], project_id: -1, testing_id: -1 });
    const [waiveUnits, setSearchData] = useFormConnect<any, PageData<AnalyzedUnit>>({}, 'waive_unit/search');

    const [config, setConfig] = useState<AnalyzedUnitConfig>();

    const editModalControl = useModalControl<boolean>(false);
    const [currentEditId, setCurrentEditId] = useState<number>();
    const journalizationModalControl = useModalControl();
    const [currentJournalizationId, setCurrentJournalizationId] = useState<number>();
    const [reloadNumber, setReloadNumber] = useState(0);
    const [productOptions, setProductOptions] = useState<DropdownItemProps[]>([]);
    const [stationOptions, setStationOptions] = useState<DropdownItemProps[]>([]);
    const [orderOptions, setOrderOptions] = useState<DropdownItemProps[]>([]);

    const [testingResultDropdownOptions, setTestingResultDropdownOptions] = useState<DropdownOption[]>([]);

    useEffect(() => {
        restRequest<DropdownOption[]>('svn_repository/result_dropdown_options').then(setTestingResultDropdownOptions)
    }, []);

    useEffect(() => {
        restRequest<AnalyzedUnitConfig>('waive_unit/config').then(setConfig);
    }, []);

    useEffect(() => {
        const options = [{ key: '', text: 'All', value: '' }, ...testingResultDropdownOptions.map(t => ({ key: t.name, text: t.name, value: t.name }))];
        setProductOptions(options);
        if (!options.find(o => o.value == searchData.product)) {
            setSearchField(null, { name: 'product', value: '' });
            setSearchField(null, { name: 'station', value: '' });
            setSearchField(null, { name: 'order', value: '' });
        }
    }, [testingResultDropdownOptions]);

    useEffect(() => {
        const product = testingResultDropdownOptions.find(t => t.name == searchData.product);
        let options: DropdownItemProps[];
        if (product) {
            options = [{ key: '', text: 'All', value: '' }, ...product.children.map(t => ({ key: t.name, text: t.name, value: t.name }))];
        } else {
            options = [{ key: '', text: 'All', value: '' }];
        }

        setOrderOptions(options);

        if (!options.find(o => o.value == searchData.station)) {
            setSearchField(null, { name: 'station', value: '' });
            setSearchField(null, { name: 'order', value: '' });
        }
    }, [testingResultDropdownOptions, searchData.product]);

    useEffect(() => {
        let options: DropdownItemProps[] = [{ key: '', text: 'All', value: '' }];
        const product = testingResultDropdownOptions.find(t => t.name == searchData.product);
        if (product) {
            const station = product.children.find(s => s.name == searchData.order);
            if (station) {
                options = [{ key: '', text: 'All', value: '' }, ...station.children.map(t => ({ key: t.name, text: t.name, value: t.name }))];
            }
        }

        setStationOptions(options);

        if (!options.find(o => o.value == searchData.order)) {
            setSearchField(null, { name: 'station', value: '' });
        }
    }, [testingResultDropdownOptions, searchData.product, searchData.order]);

    let selectedResult = 'Testing Results: All';
    out: for (const p of testingResultDropdownOptions) {
        for (const s of p.children) {
            for (const o of s.children) {
                if (o.id === searchData['result_id']) {
                    selectedResult = `${p.name} -> ${s.name} -> ${o.name}`
                    break out;
                }
            }
        }
    }

    return <>
        <Segment.Group>
            <Segment attached="top">
                <Form onSubmit={e => setSearchData(searchData)} size="mini" className="list-search">
                    <Form.Input fluid label="Product" style={{ minWidth: '10rem' }}>
                        <Dropdown name="product" value={searchData.product || ''} onChange={setSearchField} options={productOptions} search selection fluid></Dropdown>
                    </Form.Input>

                    <Form.Input label="Build" fluid style={{ minWidth: '3rem' }}>
                        <Dropdown name="order" value={searchData.order || ''} onChange={setSearchField} options={orderOptions} search selection fluid></Dropdown>
                    </Form.Input>

                    <Form.Input label="Station" fluid style={{ minWidth: '10rem' }}>
                        <Dropdown name="station" value={searchData.station || ''} onChange={setSearchField} options={stationOptions} search selection fluid></Dropdown>
                    </Form.Input>

                    <Form.Input fluid label="Serial">
                        <Input name="serial" value={searchData['serial'] || ''} onChange={setSearchField} />
                    </Form.Input>

                    <Form.Input fluid label="Measurement Date" style={{ verticalAlign: 'middle' }}>
                        <ReactDatePicker showTimeSelect={false}
                            selected={searchData['date_start'] ? new Date(searchData['date_start']) : null}
                            dateFormat="yyyy-MM-d" onChange={(d: Date) => { setSearchField(null, { name: 'date_start', value: d && d.toISOString() }) }}></ReactDatePicker>
                        <div style={{ verticalAlign: 'middle', height: '100%' }}>&nbsp;~&nbsp;</div>
                        <ReactDatePicker showTimeSelect={false}
                            selected={searchData['date_end'] ? new Date(searchData['date_end']) : null}
                            dateFormat="yyyy-MM-d" onChange={(d: Date) => { setSearchField(null, { name: 'date_end', value: d && d.toISOString() }) }}></ReactDatePicker>
                    </Form.Input>


                    <Form.Input fluid label="Measurement Status">
                        <Dropdown multiple name="measurement_status" value={searchData.measurement_status || ''} onChange={setSearchField} options={['Pass', 'Fail', 'Invalid'].map((v, i) => ({ key: i + 1, text: v, value: i + 1 }))} search selection fluid></Dropdown>
                    </Form.Input>
                    <Form.Input fluid label="Updated Status">
                        <Dropdown multiple placeholder="Updated Status" name="updated_status" value={searchData.updated_status || ''} onChange={setSearchField} options={config?.AVAILABLE_UPDATE_STATUSES.map((v, i) => ({ key: i, text: v, value: i })) || []} search selection fluid></Dropdown>
                    </Form.Input>

                    <Form.Input label="Root Cause" fluid>
                        <Input name="root_cause" value={searchData['root_cause'] || ''} onChange={setSearchField} />
                    </Form.Input>

                    <Form.Input label="Action" fluid>
                        <Input name="action" value={searchData['action'] || ''} onChange={setSearchField} />
                    </Form.Input>

                    <div>
                        <Button icon="search" type="submit"></Button>
                        <Button as="a" icon="file excel outline" onClick={(e) => {
                            e.preventDefault();
                            window.open(hostUrl('waive_unit/export?search=' + encodeURI(JSON.stringify(searchData))), '_blank');
                        }}></Button>
                    </div>
                </Form>
            </Segment>
            <Segment attached="bottom" style={{ overflow: 'auto' }}>
                <Table striped stackable>
                    <Table.Header>
                        <Table.Row>
                            <th>Product</th>
                            <th>Serial</th>
                            <th>Measurement Date</th>
                            <th>Measurement Status</th>
                            <th>Update Status</th>
                            <th>Root Cause</th>
                            <th>Action</th>
                            <th>
                                {currentUser.permissions.file_sync.create && <Button color="blue" icon="add" content="Sync" size="mini" onClick={e => {
                                    setCurrentEditId(undefined);
                                    editModalControl.setOpen(true);
                                }} />}
                            </th>
                        </Table.Row>
                    </Table.Header>
                    <Table.Body>
                        {waiveUnits?.items.map(unit => {
                            return <tr key={unit.id}>
                                <td>{unit.product || ''}</td>
                                <td>{unit.serial}</td>
                               
                                <td>{unit.root_cause}</td>
                                <td>{unit.action}</td>
                                <td>
                                    {currentUser.permissions.analyzed_unit.update && <Button onClick={e => {
                                        e.preventDefault();
                                        setCurrentEditId(unit.id);
                                        editModalControl.setOpen(true);
                                    }} color="blue" icon="edit" size="mini"></Button>}

                                    {currentUser.permissions.analyzed_unit.delete && <Button onClick={e => {
                                        dialogControl.deletionConfirm(unit.serial, argee => {
                                            if (argee) {
                                                return restRequest(`waive_unit/${unit.id}`, null, 'delete').then(() => {
                                                    setSearchData((searchData: any) => ({ ...searchData, refresh: (searchData.refresh || 1) + 1 }));
                                                })
                                            }
                                            return Promise.resolve()
                                        })
                                    }} color="red" icon="delete" size="mini" />}

                                    <Button onClick={e => {
                                        e.preventDefault();
                                        setCurrentJournalizationId(unit.id);
                                        journalizationModalControl.setOpen(true);
                                    }} icon="history" size="mini"></Button>
                                </td>
                            </tr>
                        })}
                    </Table.Body>
                </Table>
                <Page data={waiveUnits} updatePage={setSearchData}></Page>
            </Segment>
        </Segment.Group>

        <AnalyzedUnitEditor id={currentEditId} modalControl={editModalControl} onFinish={result => {
            setSearchData((searchData: any) => ({ ...searchData, refresh: (searchData.refresh || 1) + 1 }));
            editModalControl.setOpen(false)
            setReloadNumber(n => n + 1);
        }}></AnalyzedUnitEditor>

        {config && currentJournalizationId && <Journalization id={currentJournalizationId} modelControl={journalizationModalControl} config={config} />}
    </>
}


function Journalization(props: { id: number, modelControl: ModalControlData, config: AnalyzedUnitConfig }) {
    return <Modal {...props.modelControl.props} size="large">
        <Modal.Header>Journalization</Modal.Header>
        <Modal.Content>
            <JournalizationComponent containerId={props.id} containerType={1} detailFields={[{ key: 'updated_status', name: 'Updated Status', formater: (v) => props.config.AVAILABLE_UPDATE_STATUSES[v] || '' }, { key: 'root_cause', name: 'Root Cause' }, { key: 'action', name: 'Action' }, { key: 'pictures', name: 'Pictures', isList: true, formater: (v) => v['name'] }]} />
        </Modal.Content>
    </Modal>
}